// 📁 src/pages/MentorDashboard.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

// 🗓 MUI X Date Picker (inline calendar)
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateCalendar } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const timeSlots = [
  "09:00",
  "10:00",
  "11:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];

const MentorDashboard = () => {
  useAuth();

  // 🗓 original weekly-grid state (kept as-is)
  // selectedSlots structure: { "Mon-09:00": true, "Wed-15:00": true, ... }
  const [selectedSlots, setSelectedSlots] = useState({});
  const [activeTab, setActiveTab] = useState("schedule");

  const toggleSlot = (day, time) => {
    const key = `${day}-${time}`;
    setSelectedSlots((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // ⭐ NEW: calendar-based availability, per date
  // structure: { "2025-12-10": ["09:00","10:00"], "2025-12-12":["14:00"] }
  const [availability, setAvailability] = useState({});
  const [calendarDate, setCalendarDate] = useState(dayjs());

  const handleToggleCalendarSlot = (slotTime) => {
    const dateKey = calendarDate.format("YYYY-MM-DD");
    setAvailability((prev) => {
      const existing = prev[dateKey] || [];
      const updated = existing.includes(slotTime)
        ? existing.filter((t) => t !== slotTime)
        : [...existing, slotTime];
      return { ...prev, [dateKey]: updated };
    });
  };

  const handleSaveAvailability = async () => {
    try {
      await axios.post(
        "/api/mentor/availability",
        { availability },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("✅ Availability saved");
    } catch (err) {
      console.error("❌ Failed to save mentor availability:", err);
      alert("❌ Failed to save availability");
    }
  };

  const upcomingSessions = [
    {
      id: 1,
      founder: "Ankit (EdTech)",
      time: "Tue, 10:00–11:00",
      topic: "Validating MVP & GTM",
    },
    {
      id: 2,
      founder: "Riya (HealthTech)",
      time: "Thu, 15:00–16:00",
      topic: "Pricing & Business Model",
    },
  ];

  const earningsSummary = {
    month: "This Month",
    totalEarnings: "₹18,500",
    sessions: 7,
    pendingPayout: "₹5,000",
  };

  const reviews = [
    {
      founder: "Karan",
      rating: 5,
      comment: "Very clear, practical guidance on go-to-market!",
    },
    {
      founder: "Sneha",
      rating: 4,
      comment: "Helped refine my pitch deck structure.",
    },
  ];

  const profile = {
    name: "Your Mentor Profile",
    bio: "Add a short bio so founders understand your expertise and background.",
    skills: ["SaaS", "B2B", "Fundraising", "Product Strategy"],
    industries: ["EdTech", "FinTech", "HealthTech"],
    languages: ["English", "Hindi"],
    hourlyRate: "₹3,000/hour",
    meetingLink: "https://meet.google.com/your-meet-link",
  };

  // ⭐ NEW: count active calendar slots for right-side snapshot
  const totalCalendarSlots = Object.values(availability).reduce(
    (sum, slotsForDay) =>
      sum + (Array.isArray(slotsForDay) ? slotsForDay.length : 0),
    0
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "schedule":
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                📅 Upcoming Sessions
              </Typography>
              {upcomingSessions.length === 0 ? (
                <Typography color="text.secondary">
                  No sessions booked yet. Once founders book you, they’ll appear
                  here.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {upcomingSessions.map((s) => (
                    <Box
                      key={s.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #eee",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography fontWeight="600">{s.founder}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.time}
                        </Typography>
                        <Typography variant="body2">{s.topic}</Typography>
                      </Box>
                      <Button size="small" variant="outlined">
                        View details
                      </Button>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        );

      case "availability":
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                🗓 Set Your Availability
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                First, pick dates and time slots using the calendar view. Founders
                will be able to book 60-minute 1:1 video sessions only inside
                your selected slots.
              </Typography>

              {/* ⭐ NEW: Inline monthly calendar + date-wise slots (D2) */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <DateCalendar
                      value={calendarDate}
                      onChange={(newValue) =>
                        setCalendarDate(newValue || dayjs())
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ mb: 1 }} fontWeight={600}>
                      Slots for {calendarDate.format("DD MMM YYYY")}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {timeSlots.map((slot) => {
                        const dateKey = calendarDate.format("YYYY-MM-DD");
                        const isSelected =
                          availability[dateKey]?.includes(slot) || false;
                        return (
                          <Chip
                            key={slot}
                            label={slot}
                            clickable
                            color={isSelected ? "primary" : "default"}
                            onClick={() => handleToggleCalendarSlot(slot)}
                            sx={{ mb: 1 }}
                          />
                        );
                      })}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Selected slots will be available for 60-minute sessions on
                      this date.
                    </Typography>
                  </Grid>
                </Grid>
              </LocalizationProvider>

              {/* 🔁 Original weekly grid view — kept unchanged */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 1 }}
              >
                Legacy weekly view (optional): you can also mark generic weekly
                availability here for your own reference.
              </Typography>

              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ display: "inline-block", minWidth: 600 }}>
                  <Grid container spacing={1}>
                    {/* Header row */}
                    <Grid item xs={2}></Grid>
                    {days.map((day) => (
                      <Grid item xs key={day}>
                        <Typography
                          variant="body2"
                          align="center"
                          fontWeight="600"
                          sx={{ mb: 1 }}
                        >
                          {day}
                        </Typography>
                      </Grid>
                    ))}

                    {/* Time slots grid */}
                    {timeSlots.map((time) => (
                      <React.Fragment key={time}>
                        <Grid item xs={2}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: "32px" }}
                          >
                            {time}
                          </Typography>
                        </Grid>
                        {days.map((day) => {
                          const key = `${day}-${time}`;
                          const active = !!selectedSlots[key];
                          return (
                            <Grid item xs key={key}>
                              <Box
                                onClick={() => toggleSlot(day, time)}
                                sx={{
                                  cursor: "pointer",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: active ? "primary.main" : "#ddd",
                                  backgroundColor: active
                                    ? "primary.light"
                                    : "#fafafa",
                                  height: 32,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {active ? "Available" : ""}
                              </Box>
                            </Grid>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </Grid>
                </Box>
              </Box>

              <Box sx={{ textAlign: "right", mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveAvailability}
                >
                  Save Availability
                </Button>
              </Box>
            </CardContent>
          </Card>
        );

      case "earnings":
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                💰 Earnings Overview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {earningsSummary.month}
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                {earningsSummary.totalEarnings}
              </Typography>
              <Typography variant="body2">
                Sessions completed:{" "}
                <strong>{earningsSummary.sessions}</strong>
              </Typography>
              <Typography variant="body2">
                Pending payout:{" "}
                <strong>{earningsSummary.pendingPayout}</strong>
              </Typography>

              <Button sx={{ mt: 2 }} variant="outlined" size="small">
                View detailed report (coming soon)
              </Button>
            </CardContent>
          </Card>
        );

      case "reviews":
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                ⭐ Reviews from Founders
              </Typography>
              {reviews.length === 0 ? (
                <Typography color="text.secondary">
                  You don’t have reviews yet. Once you complete sessions,
                  reviews will appear here.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {reviews.map((r, idx) => (
                    <Box
                      key={idx}
                      sx={{ p: 2, borderRadius: 2, border: "1px solid #eee" }}
                    >
                      <Typography fontWeight="600">{r.founder}</Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {"⭐".repeat(r.rating)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {r.comment}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        );

      case "profile":
      default:
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                👤 Mentor Profile
              </Typography>
              <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
                {profile.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {profile.bio}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Skills
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 2 }}
                  >
                    {profile.skills.map((s) => (
                      <Chip key={s} label={s} size="small" />
                    ))}
                  </Stack>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Industries
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 2 }}
                  >
                    {profile.industries.map((ind) => (
                      <Chip key={ind} label={ind} size="small" />
                    ))}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Languages
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 2 }}
                  >
                    {profile.languages.map((lang) => (
                      <Chip key={lang} label={lang} size="small" />
                    ))}
                  </Stack>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Hourly Rate
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    {profile.hourlyRate}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Default Meeting Link
                  </Typography>
                  <Typography variant="body2" color="primary.main">
                    {profile.meetingLink}
                  </Typography>
                </Grid>
              </Grid>

              <Button sx={{ mt: 2 }} variant="outlined" size="small">
                Edit profile (Phase-2: connect to backend form)
              </Button>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          🎓 Mentor Dashboard
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Manage your sessions, availability, earnings, and mentor profile.
        </Typography>
      </motion.div>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Tabs */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {["schedule", "availability", "earnings", "reviews", "profile"].map(
              (tab) => (
                <Chip
                  key={tab}
                  label={
                    tab === "schedule"
                      ? "My Schedule"
                      : tab === "availability"
                      ? "Availability"
                      : tab === "earnings"
                      ? "Earnings"
                      : tab === "reviews"
                      ? "Reviews"
                      : "Profile"
                  }
                  clickable
                  color={activeTab === tab ? "primary" : "default"}
                  onClick={() => setActiveTab(tab)}
                />
              )
            )}
          </Stack>

          {renderTabContent()}
        </Grid>

        {/* Right Side: AI Tips & Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                🤖 AI Tip for Mentors
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add specific outcomes to your profile (e.g. “I help founders
                refine their GTM in 3 sessions”) to increase booking rates. In
                future, this will be AI-personalised.
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                📊 Snapshot
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">
                Total upcoming sessions:{" "}
                <strong>{upcomingSessions.length}</strong>
              </Typography>
              <Typography variant="body2">
                Active availability slots:{" "}
                <strong>{totalCalendarSlots}</strong>
              </Typography>
              <Typography variant="body2">
                Monthly earnings (preview):{" "}
                <strong>{earningsSummary.totalEarnings}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MentorDashboard;
